import i18n from '@/i18n';
import { format } from 'date-fns';
import {
  taskAPI,
  templateAPI,
  bumpImageSpaceUsageCounts,
  imageSpaceAPI,
} from '@/utils/api';
import {
  inlineUrlFromGoodsImageRef,
  normalizeGoodsImageRefToId,
  resolveStampAreasForSlide,
  resolveUrlFromImageSpaceRecord,
} from './goodsTemplateImageUrls';
import { toProcessableImageUrl, persistProcessedImageToSpace, saveWorkflowImageToSpace, attachWorkflowImagePreview } from './sourcingWorkflowSpace';
import { beginWorkflowFeatureTask, failWorkflowFeatureTask } from './workflowFeatureTask';
import { GOODS_STUDIO_DEFAULT_PARAMS } from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { resolveGoodsStudioWorkflowParams } from '@/pages/ipe/workbench/utils/goodsStudioPromptCaseLoader';
import {
  isPersistableImg2ImgApiUrl,
  resolveImg2ImgReferenceForApi,
} from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { waitForFundingTaskComplete, extractUrlsFromFundingTask } from './waitForFundingTask';
import { buildPipelineReferenceResult } from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';
import { runGoodsFormsSequentialComposite } from './goodsSequentialComposite';
import { hydrateTemplateCanvases } from './hydrateTemplateCanvases';
import { isGoodsTemplateServerWorkerEnabled } from './goodsTemplateServerWorkerFlag';
import { getIpeLayersForSlide } from '@/image-designer/adapters/ipeLayerAdapter';
import { resolveStampSlotNamesPerSkc } from '@/pages/ipe/workbench/utils/podTemplateMeta';
import { groupGoodsCompositeResultsByMain } from '@/pages/ipe/workbench/utils/groupGoodsCompositeResultsByMain';
import {
  buildColorSlotsForGoodsPrintGroup,
  pickGoodsCompositeUrl,
} from '@/pages/ipe/workbench/utils/listingBridgeColorSlots';

function collectOrderedStampNames(templateData) {
  const seen = new Set();
  const names = [];
  const add = (raw) => {
    const n = String(raw || '').trim() || '未命名印花';
    if (seen.has(n)) return;
    seen.add(n);
    names.push(n);
  };
  const areas = templateData?.stampAreas && typeof templateData.stampAreas === 'object'
    ? templateData.stampAreas
    : {};
  Object.keys(areas).forEach((key) => {
    resolveStampAreasForSlide(key, areas).forEach((stamp) => add(stamp?.name));
  });
  const images = Array.isArray(templateData?.images) ? templateData.images : [];
  images.forEach((slide) => {
    if (!slide?.id) return;
    resolveStampAreasForSlide(slide.id, areas).forEach((stamp) => add(stamp?.name));
    getIpeLayersForSlide(slide.id, templateData).forEach((layer) => add(layer?.name));
  });
  const layers = templateData?.ipeLayers && typeof templateData.ipeLayers === 'object'
    ? templateData.ipeLayers
    : {};
  Object.values(layers).forEach((arr) => {
    (Array.isArray(arr) ? arr : []).forEach((layer) => add(layer?.name));
  });
  return names;
}

async function buildImageUrlMapFromGoodsForms(forms = []) {
  const map = {};
  for (const form of forms) {
    for (const ig of form.imageGroups || []) {
      for (const ref of Object.values(ig || {})) {
        const id = normalizeGoodsImageRefToId(ref);
        if (!id || map[id]) continue;
        const inline = inlineUrlFromGoodsImageRef(ref);
        if (inline) {
          const processable = await toProcessableImageUrl(inline);
          if (processable) map[id] = processable;
          continue;
        }
        try {
          const res = await imageSpaceAPI.getById(id);
          const rec = res?.image || res?.data || res;
          const raw = await resolveUrlFromImageSpaceRecord(rec);
          if (raw) {
            const processable = await toProcessableImageUrl(raw);
            if (processable) map[id] = processable;
          }
        } catch {
          /* ignore */
        }
      }
    }
  }
  return map;
}

function collectPrintIdsFromGoodsForms(forms = []) {
  const ids = new Set();
  forms.forEach((form) => {
    (form.imageGroups || []).forEach((ig) => {
      Object.values(ig || {}).forEach((ref) => {
        const id = normalizeGoodsImageRefToId(ref);
        if (id) ids.add(id);
      });
    });
  });
  return [...ids];
}

function estimateUnitsFromGoodsForms(forms = []) {
  let estimatedUnits = 0;
  forms.forEach((form) => {
    const carousel = Array.isArray(form.templateData?.images) ? form.templateData.images : [];
    const stampAreas = form.templateData?.stampAreas || {};
    (form.imageGroups || []).forEach((imageGroup) => {
      carousel.forEach((slide) => {
        const slideId = slide?.id;
        if (!slideId) return;
        const areas = resolveStampAreasForSlide(slideId, stampAreas);
        if (areas.length === 0) return;
        const required = new Set(areas.map((s) => s.name).filter(Boolean));
        const ok = [...required].every((n) => imageGroup[n]);
        if (ok) estimatedUnits += 1;
      });
    });
  });
  return estimatedUnits;
}

function persistedImageRef(ref) {
  if (!ref || typeof ref !== 'object') return ref;
  const url = String(ref.url || '').trim();
  const inline = !url || url.startsWith('data:') || url.startsWith('blob:');
  return { ...ref, url: inline ? '' : url };
}

function slimGoodsFormForTask(form) {
  const td = form.templateData || {};
  return {
    templateId: form.templateId,
    templateName: form.templateName,
    imageGroups: (form.imageGroups || []).map((group) => {
      const next = {};
      Object.entries(group || {}).forEach(([key, ref]) => {
        next[key] = persistedImageRef(ref);
      });
      return next;
    }),
    compositeResults: [],
    templateData: {
      images: Array.isArray(td.images) ? td.images : [],
      stampAreas: td.stampAreas && typeof td.stampAreas === 'object' ? td.stampAreas : {},
      ipeLayers: td.ipeLayers && typeof td.ipeLayers === 'object' ? td.ipeLayers : {},
      primaryImageId: td.primaryImageId,
      canvasStorage: td.canvasStorage,
      slideCanvasIds: Array.isArray(td.slideCanvasIds) ? td.slideCanvasIds : [],
    },
  };
}

function previewUrlForTask(raw) {
  const url = String(raw || '').trim();
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return '';
  return url;
}

function collectCompositeUrlsForGroup(finalForms, groupIndex) {
  const urls = [];
  (finalForms || []).forEach((form) => {
    (form.compositeResults || []).forEach((result) => {
      if (Number(result?.groupIndex) !== Number(groupIndex)) return;
      const url = String(result.compositeUrl || '').trim();
      if (url) urls.push(url);
    });
  });
  return urls;
}

/**
 * 仅主图合成结果（与商品设计详情「合成图」一致，不含其下「细节图」）。
 * 带颜色元数据，供主图创作按色位逐张带入并加保色提示。
 * 必须覆盖模板全部主图色位（黑/白等），不能只取第一张。
 */
export function collectMainCompositeEntriesForGroup(finalForms, groupIndex) {
  const entries = [];
  const seen = new Set();
  const pushEntry = (raw, meta = {}) => {
    const url = String(raw || '').trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    entries.push({
      url,
      colorLabel: String(meta.colorLabel || '').trim(),
      colorCode: String(meta.colorCode || '').trim(),
      mainName: String(meta.mainName || '').trim(),
    });
  };

  (finalForms || []).forEach((form, formIndex) => {
    // 1) 与入库色槽一致：按模板主图 SKC 收集（最完整）
    const colorSlots = buildColorSlotsForGoodsPrintGroup(form, groupIndex, {
      formIndex,
      billingNav: 'podProductDesign',
    });
    (colorSlots || []).forEach((slot) => {
      pushEntry(slot?.mainUrl, {
        colorLabel: slot?.colorLabel,
        colorCode: slot?.colorCode,
        mainName: slot?.mainName || slot?.patternName,
      });
    });

    // 2) 层级分组补齐（跳过「细节冒充主图」的 fallback）
    const { groups } = groupGoodsCompositeResultsByMain(form, groupIndex, {
      billingNav: 'podProductDesign',
    });
    (groups || []).forEach((slot) => {
      if (slot?.mainAsDetailFallback) return;
      const tpl = slot?.main?.templateImage || {};
      pushEntry(pickGoodsCompositeUrl(slot?.main?.result), {
        colorLabel: tpl.colorLabel,
        colorCode: tpl.colorCode,
        mainName: tpl.name || slot?.main?.result?.name,
      });
    });

    // 3) 显式 kind/role=main 再扫一遍，避免早退漏色
    (form.compositeResults || []).forEach((result) => {
      if (Number(result?.groupIndex) !== Number(groupIndex)) return;
      const kind = String(result?.kind || result?.role || '').trim().toLowerCase();
      if (kind !== 'main') return;
      pushEntry(pickGoodsCompositeUrl(result) || result?.compositeUrl, {
        mainName: result?.name,
      });
    });
  });
  return entries;
}

/** @returns {string[]} 仅 URL，兼容旧调用 */
export function collectMainCompositeUrlsForGroup(finalForms, groupIndex) {
  return collectMainCompositeEntriesForGroup(finalForms, groupIndex).map((row) => row.url);
}

function formatTemplateGoodsError(error, stepLabel) {
  const raw = String(error?.message || error || '').trim();
  if (!raw) return i18n.t('workbench:ipeUtilError.stepFailed', { step: stepLabel });
  if (raw.includes(stepLabel)) return raw;
  if (/is not a function|is not iterable|Cannot convert .* to object|Cannot read properties of undefined/i.test(raw)) {
    return i18n.t('workbench:ipeUtilError.stepFailedTemplateData', { step: stepLabel });
  }
  return raw.startsWith(stepLabel) ? raw : `${stepLabel}：${raw}`;
}

async function loadHydratedGoodsTemplate(tid, stepLabel) {
  const res = await templateAPI.getById(tid);
  let template = res?.template || res?.data;
  if (!template || !template.id) {
    throw new Error(i18n.t('workbench:ipeUtilError.templateMissing', { step: stepLabel, id: tid }));
  }
  try {
    template = await hydrateTemplateCanvases(template);
  } catch {
    /* 使用接口返回的内嵌数据 */
  }
  return template;
}

async function executeTemplateGoodsComposite({
  preliminaryForms,
  printIds,
  previewSource,
  billingNav,
  workflowContext,
  extraInitialResult = {},
  stepLabel,
  groupCount,
  imageUrlMap = {},
}) {
  const total = Math.max(1, groupCount || estimateUnitsFromGoodsForms(preliminaryForms) || preliminaryForms.length);
  const previewForTask = previewUrlForTask(previewSource);
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'goods',
    taskName: billingNav === 'podProductDesign' ? 'POD商品设计任务' : '主图创作任务',
    imageIds: printIds,
    previewUrl: previewForTask || undefined,
    estimatedTotal: total,
    workflowContext,
    initialResult: {
      previewUrl: previewForTask || undefined,
      imageIds: printIds,
      sourceNav: billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods',
      billingNavId: billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods',
      goodsForms: preliminaryForms.map(slimGoodsFormForTask),
      ...(isGoodsTemplateServerWorkerEnabled()
        ? { serverWorker: true, batchPhase: 'queued' }
        : {}),
      ...extraInitialResult,
    },
  });

  try {
    await bumpImageSpaceUsageCounts(printIds);
  } catch {
    /* ignore */
  }

  if (isGoodsTemplateServerWorkerEnabled()) {
    try {
      await taskAPI.update(taskId, {
        status: 'processing',
        result: {
          ...initialResult,
          serverWorker: true,
          batchPhase: 'queued',
        },
      });
      await taskAPI.submit(taskId);
      const completed = await waitForFundingTaskComplete(taskId, {
        intervalMs: 2000,
        timeoutMs: 1_800_000,
      });
      const finalForms = (completed?.goodsForms?.length
        ? completed.goodsForms
        : completed?.result?.goodsForms) || preliminaryForms;
      const successCount = Number(completed?.successCount ?? completed?.result?.successCount ?? 0) || 0;
      if (successCount === 0) {
        const hint = completed?.result?.failureHint || i18n.t('workbench:ipeUtilError.noCompositeHint');
        throw new Error(
          i18n.t('workbench:ipeUtilError.stepFailedWithHint', { step: stepLabel, hint }),
        );
      }
      return { taskId, finalForms };
    } catch (error) {
      const wrapped = new Error(formatTemplateGoodsError(error, stepLabel));
      await failWorkflowFeatureTask(taskId, initialResult, total, wrapped);
      throw wrapped;
    }
  }

  try {
    const sequentialResult = await runGoodsFormsSequentialComposite({
      effectiveGoodsForms: preliminaryForms,
      taskId,
      billingNav,
      imageSpaceImages: [],
      goodsImageUrlSnapshot: imageUrlMap,
    });
    const {
      finalForms,
      formsWithReferences,
      successCount,
      failedCount,
      finalStatus,
      failureHint,
      finishedAt,
    } = sequentialResult;

    await taskAPI.update(taskId, {
      status: finalStatus,
      total,
      successCount,
      failedCount,
      finishedAt: finishedAt || format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: {
        ...initialResult,
        total,
        successCount,
        failedCount,
        failureHint: failureHint || undefined,
        goodsForms: formsWithReferences,
      },
    });

    if (successCount === 0) {
      throw new Error(
        i18n.t('workbench:ipeUtilError.stepFailedWithHint', { step: stepLabel, hint: failureHint || i18n.t('workbench:ipeUtilError.noCompositeHint') }),
      );
    }

    return { taskId, finalForms };
  } catch (error) {
    const wrapped = new Error(formatTemplateGoodsError(error, stepLabel));
    if (!(error?.message && /未生成合成图|no composite was generated/i.test(String(error.message)))) {
      await failWorkflowFeatureTask(taskId, initialResult, total, wrapped);
    }
    throw wrapped;
  }
}

function pickGoodsStudioParams(params = {}) {
  const studio = { ...GOODS_STUDIO_DEFAULT_PARAMS };
  Object.keys(GOODS_STUDIO_DEFAULT_PARAMS).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null) {
      studio[key] = params[key];
    }
  });
  return studio;
}

function buildAiGoodsWorkflowEstimatedTotal(studioParams) {
  const mainCount = Math.max(1, Number(studioParams?.mainImageCount) || 1);
  const detailCount = Math.max(0, Number(studioParams?.detailPageCount) || 0);
  return Math.max(1, mainCount + detailCount);
}

function goodsWorkflowStepLabel(billingNav) {
  return billingNav === 'podProductDesign' ? 'POD商品设计' : '主图创作';
}

/** 与功能页同一套参考图解析：/uploads/ 与公网 http 原样提交，勿在浏览器再转一层。 */
async function resolveGoodsStudioStampUrl(printImageUrl, stepLabel) {
  const raw = String(printImageUrl || '').trim();
  if (!raw) throw new Error(i18n.t('workbench:ipeUtilError.stepMissingImageUrl', { step: stepLabel }));
  let stampUrl = await resolveImg2ImgReferenceForApi(raw);
  if (!stampUrl || !isPersistableImg2ImgApiUrl(stampUrl)) {
    stampUrl = await resolveImg2ImgReferenceForApi(await toProcessableImageUrl(raw));
  }
  if (!stampUrl || !isPersistableImg2ImgApiUrl(stampUrl)) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepImageNotForGoods', { step: stepLabel }));
  }
  return String(stampUrl).trim();
}

function pickGoodsStudioFailureHint(task, fallback = '主图创作失败') {
  const result = task?.result && typeof task.result === 'object' ? task.result : {};
  const hint = String(result.failureHint || result.error || '').trim();
  if (hint && hint !== '生成失败') return hint;
  const outputs = Array.isArray(result.outputs) ? result.outputs : [];
  const failed = outputs.find((row) => (
    String(row?.status || '').toLowerCase() === 'failed' && String(row?.error || '').trim()
  ));
  if (failed) {
    const outputError = String(failed.error || '').trim();
    if (outputError && outputError !== '生成失败') return outputError;
  }
  return fallback;
}

/** 主图创作（使用流水线当前图，不走 POD 套图） */
export async function runGoodsAiStudioWorkflowStep({
  row,
  printImageUrl,
  params = {},
  workflowContext = {},
  billingNav = 'goods',
}) {
  const stepLabel = goodsWorkflowStepLabel(billingNav);
  if (!printImageUrl || !String(printImageUrl).trim()) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepMissingImageUrl', { step: stepLabel }));
  }
  const studioParams = {
    ...pickGoodsStudioParams(params),
    ...(await resolveGoodsStudioWorkflowParams(params)),
  };
  const stampUrl = await resolveGoodsStudioStampUrl(printImageUrl, stepLabel);
  const savedInput = await saveWorkflowImageToSpace(
    `wf-goods-in-${row.id}`,
    stampUrl,
    '工作流-主图创作输入',
    { navId: 'goods', ingestKind: 'source' },
  );
  const stampId = savedInput.spaceId;
  const stampDisplayName = String(row?.title || row?.name || '').trim();
  const placeholderForms = [{
    templateId: 'goods-studio-ai',
    templateName: '主图创作',
    imageGroups: [{
      印花1: {
        id: stampId,
        url: stampUrl,
        ...(stampDisplayName ? { name: stampDisplayName } : {}),
        role: 'print',
      },
    }],
    compositeResults: [],
  }];

  const estimatedTotal = buildAiGoodsWorkflowEstimatedTotal(studioParams);
  const sourceNav = billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';

  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'goods',
    taskName: billingNav === 'podProductDesign' ? 'POD商品设计任务' : '主图创作任务',
    imageIds: [stampId],
    previewUrl: stampUrl,
    estimatedTotal,
    workflowContext,
    initialResult: {
      composeMode: 'ai',
      generationMode: 'goodsStudioAi',
      sourceNav,
      billingNavId: sourceNav,
      studioParams: { ...studioParams },
      ...buildPipelineReferenceResult({
        imageUrl: stampUrl,
        imageId: stampId,
        previewUrl: stampUrl,
      }),
      stampPreviews: [stampUrl],
      stampSourceUrls: [stampUrl],
      stampCount: 1,
      goodsPrintUrlByImageId: { [stampId]: stampUrl },
      imageIds: [stampId],
      goodsForms: placeholderForms,
      printSource: sourceNav === 'podProductDesign' ? 'workflow-stamp-extract' : 'workflow-goods-studio',
      serverWorker: true,
      batchPhase: 'queued',
      mainImageCount: studioParams.mainImageCount,
      detailPageCount: studioParams.detailPageCount,
      outputs: [],
      urls: [],
      processedStampIndices: [],
    },
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    const urls = extractUrlsFromFundingTask(task);
    if (status === 'failed' || status === 'canceled' || status === 'cancelled' || urls.length === 0) {
      throw new Error(pickGoodsStudioFailureHint(
        task,
        urls.length === 0 ? '主图创作完成但未返回可用图片' : '主图创作失败',
      ));
    }
    const outputUrl = urls[0];
    const saved = await persistProcessedImageToSpace(
      `wf-goods-ai-${row.id}`,
      outputUrl,
      '工作流-主图创作',
      { navId: 'goods' },
    );
    return {
      taskId,
      mainImageCount: Math.max(1, Number(studioParams.mainImageCount) || 1),
      detailPageCount: Math.max(0, Number(studioParams.detailPageCount) || 0),
      ...attachWorkflowImagePreview(saved, urls.length > 0 ? urls : [outputUrl]),
    };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, estimatedTotal, error);
    throw error;
  }
}

export function shouldUseAiGoodsStudio(billingNav, params = {}) {
  if (String(billingNav || '').trim() === 'podProductDesign') return false;
  if (billingNav === 'goods') return true;
  if (String(params.composeMode || '').toLowerCase() === 'ai') return true;
  const templateIds = Array.isArray(params.imageTemplateIds)
    ? params.imageTemplateIds.map((x) => String(x).trim()).filter(Boolean)
    : [];
  return templateIds.length === 0;
}

/**
 * 工作流套图合成（仅 POD 商品设计）：按 imageTemplateIds 拉取设计模板，
 * 用印花图填满印花位并合成。主图创作不会进入此路径。
 */
export async function runGoodsWorkflowStep({
  row,
  printSpaceId,
  printImageUrl,
  params = {},
  workflowContext = {},
  assignment = null,
  billingNav = 'goods',
}) {
  const useAiGoods = shouldUseAiGoodsStudio(billingNav, params);
  if (useAiGoods) {
    return runGoodsAiStudioWorkflowStep({
      row,
      printSpaceId,
      printImageUrl,
      params,
      workflowContext,
      billingNav,
    });
  }

  const templateIds = Array.isArray(params.imageTemplateIds)
    ? [...new Set(params.imageTemplateIds.map((x) => String(x).trim()).filter(Boolean))]
    : [];
  if (templateIds.length === 0) {
    if (billingNav === 'podProductDesign') {
      throw new Error(i18n.t('workbench:ipeUtilError.podSelectTemplate'));
    }
    return runGoodsAiStudioWorkflowStep({
      row,
      printSpaceId,
      printImageUrl,
      params,
      workflowContext,
      billingNav,
    });
  }

  const stepLabel = goodsWorkflowStepLabel(billingNav);
  const manualForms = Array.isArray(assignment?.goodsForms) ? assignment.goodsForms : [];
  const useManualAssignment = manualForms.length > 0;

  if (!useManualAssignment) {
    if (!printSpaceId || !String(printSpaceId).trim()) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepMissingInputImage', { step: stepLabel }));
    }
    if (!printImageUrl || !String(printImageUrl).trim()) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepMissingImageUrl', { step: stepLabel }));
    }
  }

  let preliminaryForms = [];
  let imageUrlMap = {};
  let printIds = [];

  if (useManualAssignment) {
    preliminaryForms = manualForms;
    imageUrlMap = await buildImageUrlMapFromGoodsForms(manualForms);
    printIds = collectPrintIdsFromGoodsForms(manualForms);
    if (printIds.length === 0) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepManualAssignEmpty', { step: stepLabel }));
    }
    if (Object.keys(imageUrlMap).length === 0) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepManualAssignUnresolved', { step: stepLabel }));
    }
  } else {
    const printId = String(printSpaceId);
    const processable = await toProcessableImageUrl(printImageUrl);
    if (!processable) throw new Error(i18n.t('workbench:ipeUtilError.stepStampResultUnresolved', { step: stepLabel }));

    imageUrlMap = { [printId]: processable };
    printIds = [printId];

    for (const tid of templateIds) {
      const template = await loadHydratedGoodsTemplate(tid, stepLabel);
      const td = template.templateData || {};
      const names = resolveStampSlotNamesPerSkc(template);
      const slotNames = names.length > 0 ? names : collectOrderedStampNames(td);
      if (slotNames.length === 0) {
        console.warn('[workflow goods] skip template without stamp areas', tid);
        continue;
      }
      preliminaryForms.push({
        templateId: String(template.id),
        templateName: template.name || `模版 ${template.id}`,
        templateData: td,
        imageGroups: [buildFilledStampGroup(slotNames, {
          spaceId: printId,
          url: printImageUrl,
          name: String(row?.title || row?.name || '').trim(),
        })],
        compositeResults: [],
      });
    }
  }

  if (preliminaryForms.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepNoStampAreasOrAssign', { step: stepLabel }));
  }

  const previewSource = previewUrlForTask(printImageUrl) || previewUrlForTask(imageUrlMap[printIds[0]]);
  const { taskId, finalForms } = await executeTemplateGoodsComposite({
    preliminaryForms,
    printIds,
    previewSource,
    billingNav,
    workflowContext,
    extraInitialResult: {
      printSource: useManualAssignment ? 'workflow-manual-stamp-assign' : 'workflow-stamp-extract',
    },
    stepLabel,
    groupCount: Math.max(1, estimateUnitsFromGoodsForms(preliminaryForms) || preliminaryForms.length),
    imageUrlMap,
  });

  // 预分配多印花组时 finalForms 含多组；按组收集主图，供各 listing 行取用
  const imageGroupCount = Math.max(
    1,
    ...preliminaryForms.map((form) => (
      Array.isArray(form.imageGroups) ? form.imageGroups.length : 0
    )),
  );
  const mainsByListingGroup = [];
  for (let gi = 0; gi < imageGroupCount; gi += 1) {
    mainsByListingGroup.push(collectMainCompositeEntriesForGroup(finalForms, gi));
  }
  const primaryEntries = mainsByListingGroup[0] || [];
  const mainCompositeImageUrls = primaryEntries.map((row) => row.url);
  const compositeImageUrls = collectCompositeUrlsForGroup(finalForms, 0);
  if (compositeImageUrls.length === 0 && mainCompositeImageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepFailedNoComposite', { step: stepLabel }));
  }
  const previewUrl = mainCompositeImageUrls[0] || compositeImageUrls[0];
  const saved = await saveWorkflowImageToSpace(
    `wf-goods-${row.id}`,
    previewUrl,
    '工作流-套图',
  );

  return {
    taskId,
    listingGroupIndex: 0,
    mainCompositeImageUrls,
    mainCompositeMetas: primaryEntries,
    mainsByListingGroup,
    ...attachWorkflowImagePreview(saved, compositeImageUrls.length ? compositeImageUrls : mainCompositeImageUrls),
  };
}

/* ---------------------------------------------------------------------------
 * 「一个模块一个批次」批量执行
 * 一个工作流任务在同一模块里只创建一个批次任务：把全部印花组作为该批次的多条
 * source 一起提交，跑完再按 sourceIndex 把产出切回各组。
 * ------------------------------------------------------------------------- */

/** 按 sourceIndex 把 funding task 的 outputs / urls 切回各条 source。 */
function splitUrlsBySourceIndex(result, count) {
  const buckets = Array.from({ length: count }, () => []);
  const outputs = Array.isArray(result?.outputs) ? result.outputs : [];
  outputs.forEach((row, idx) => {
    const url = String(row?.url || row?.outputUrl || row?.imageUrl || '').trim();
    if (!url) return;
    if (String(row?.status || '').toLowerCase() === 'failed') return;
    let si = Number(row?.sourceIndex);
    if (!Number.isFinite(si) || si < 0 || si >= count) {
      si = outputs.length === count ? idx : 0;
    }
    buckets[si].push(url);
  });
  const hasAny = buckets.some((list) => list.length > 0);
  if (!hasAny) {
    const all = extractUrlsFromFundingTask(result);
    all.forEach((u, i) => {
      if (!u) return;
      buckets[Math.min(i, count - 1)].push(u);
    });
  }
  return buckets;
}

/** 单张印花灌满模板全部印花位（仅「一张素材对应一套前后印」时使用）。 */
function buildFilledStampGroup(slotNames = [], group) {
  const stampName = String(group?.name || group?.title || '').trim();
  const ref = {
    id: String(group.spaceId),
    url: String(group.url),
    role: 'print',
    ...(stampName ? { name: stampName } : {}),
  };
  const imageGroup = {};
  slotNames.forEach((name) => {
    imageGroup[name] = ref;
  });
  return imageGroup;
}

function toStampSlotRef(group) {
  const stampName = String(group?.name || group?.title || '').trim();
  return {
    id: String(group.spaceId),
    url: String(group.url),
    role: 'print',
    ...(stampName ? { name: stampName } : {}),
  };
}

/**
 * 按印花位数量把素材切成多组：
 * 2 个印花位 + [A,B,C,D] → [{正面:A,背面:B}, {正面:C,背面:D}]
 * 禁止把同一张印花复制进一组的所有印花位（除非本组素材不足才复用首张）。
 */
function buildChunkedStampImageGroups(slotNames = [], materials = []) {
  const slots = (Array.isArray(slotNames) ? slotNames : []).filter(Boolean);
  const list = (Array.isArray(materials) ? materials : []).filter(
    (g) => g && g.spaceId && String(g.url || '').trim(),
  );
  if (list.length === 0) return [];

  // 单印花位：每张素材各自一组
  if (slots.length <= 1) {
    const slot = slots[0] || '印花1';
    return list.map((group) => ({ [slot]: toStampSlotRef(group) }));
  }

  // 多素材但只够「每组一张」时：才允许同一张灌满全套印花位
  if (list.length === 1) {
    return [buildFilledStampGroup(slots, list[0])];
  }

  const slotCount = slots.length;
  const groupCount = Math.ceil(list.length / slotCount);
  const imageGroups = [];
  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
    const imageGroup = {};
    const groupStart = groupIndex * slotCount;
    slots.forEach((slotName, slotIndex) => {
      let materialIndex = groupStart + slotIndex;
      if (materialIndex >= list.length) {
        materialIndex = groupStart < list.length ? groupStart : list.length - 1;
      }
      imageGroup[slotName] = toStampSlotRef(list[materialIndex]);
    });
    imageGroups.push(imageGroup);
  }
  return imageGroups;
}

/**
 * 主图创作（AI 工作室）批量：一个任务提交 N 条参考图，worker 按 sourceIndex 逐张出图。
 */
async function runGoodsAiBatchWorkflowStep({ groups, params = {}, workflowContext = {}, billingNav = 'goods' }) {
  const stepLabel = goodsWorkflowStepLabel(billingNav);
  const validGroups = (groups || []).filter((g) => g && String(g.url || '').trim());
  if (validGroups.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepMissingImageUrl', { step: stepLabel }));
  }
  const fromPodMains = validGroups.some((g) => g && g.__fromPodMains);
  const studioParams = {
    ...pickGoodsStudioParams(params),
    ...(await resolveGoodsStudioWorkflowParams(params)),
    // POD 各颜色主图已展开为多条 source 时，每条固定出 1 张主图，避免再 × mainImageCount
    ...(fromPodMains ? { mainImageCount: 1, detailPageCount: 0 } : {}),
  };
  const stampUrls = [];
  const stampIds = [];
  const stampColorHints = [];
  for (let gi = 0; gi < validGroups.length; gi += 1) {
    const group = validGroups[gi];
    const stampUrl = await resolveGoodsStudioStampUrl(group.url, stepLabel);
    const slotKey = Number.isFinite(Number(group.__podMainSlot))
      ? `m${Number(group.__podMainSlot)}`
      : `i${gi}`;
    const savedInput = await saveWorkflowImageToSpace(
      `wf-goods-in-${group.rowId ?? gi}-${slotKey}`,
      stampUrl,
      '工作流-主图创作输入',
      { navId: 'goods', ingestKind: 'source' },
    );
    stampUrls.push(stampUrl);
    stampIds.push(savedInput.spaceId);
    const colorHint = String(
      group.__colorLabel || group.__colorCode || group.__mainName || '',
    ).trim();
    stampColorHints.push(colorHint);
  }

  const perStampUnits = buildAiGoodsWorkflowEstimatedTotal(studioParams);
  const total = Math.max(1, perStampUnits * validGroups.length);
  const sourceNav = billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';
  const placeholderForms = validGroups.map((group, i) => {
    const stampDisplayName = String(group?.name || group?.title || '').trim();
    return {
      templateId: 'goods-studio-ai',
      templateName: '主图创作',
      imageGroups: [{
        印花1: {
          id: stampIds[i],
          url: stampUrls[i],
          ...(stampDisplayName ? { name: stampDisplayName } : {}),
          role: 'print',
        },
      }],
      compositeResults: [],
    };
  });

  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'goods',
    taskName: billingNav === 'podProductDesign' ? 'POD商品设计任务' : '主图创作任务',
    imageIds: stampIds,
    previewUrl: stampUrls[0],
    estimatedTotal: total,
    workflowContext,
    initialResult: {
      composeMode: 'ai',
      generationMode: 'goodsStudioAi',
      sourceNav,
      billingNavId: sourceNav,
      studioParams: { ...studioParams },
      ...buildPipelineReferenceResult({
        imageUrl: stampUrls[0],
        imageId: stampIds[0],
        previewUrl: stampUrls[0],
      }),
      stampPreviews: stampUrls,
      stampSourceUrls: stampUrls,
      stampColorHints,
      stampCount: stampUrls.length,
      goodsPrintUrlByImageId: stampIds.reduce((acc, id, i) => {
        acc[id] = stampUrls[i];
        return acc;
      }, {}),
      imageIds: stampIds,
      goodsForms: placeholderForms,
      printSource: sourceNav === 'podProductDesign' ? 'workflow-stamp-extract' : 'workflow-goods-studio',
      serverWorker: true,
      batchPhase: 'queued',
      mainImageCount: studioParams.mainImageCount,
      detailPageCount: studioParams.detailPageCount,
      outputs: [],
      urls: [],
      processedStampIndices: [],
    },
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    const urls = extractUrlsFromFundingTask(task);
    if (status === 'failed' || status === 'canceled' || status === 'cancelled' || urls.length === 0) {
      throw new Error(pickGoodsStudioFailureHint(
        task,
        urls.length === 0 ? '主图创作完成但未返回可用图片' : '主图创作失败',
      ));
    }
    const bySource = splitUrlsBySourceIndex(task?.result || {}, validGroups.length);
    const byGroup = [];
    for (let i = 0; i < validGroups.length; i += 1) {
      const groupUrls = bySource[i];
      if (!groupUrls.length) {
        byGroup.push({
          rowId: validGroups[i].rowId,
          taskId,
          failed: true,
          error: i18n.t('workbench:ipeUtilError.stampGroupNoImage'),
        });
        continue;
      }
      const saved = await persistProcessedImageToSpace(
        `wf-goods-ai-${validGroups[i].rowId ?? i}`,
        groupUrls[0],
        '工作流-主图创作',
        { navId: 'goods' },
      );
      byGroup.push({
        rowId: validGroups[i].rowId,
        taskId,
        mainImageCount: Math.max(1, Number(studioParams.mainImageCount) || 1),
        detailPageCount: Math.max(0, Number(studioParams.detailPageCount) || 0),
        ...attachWorkflowImagePreview(saved, groupUrls),
      });
    }
    return { taskId, byGroup };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, total, error);
    throw error;
  }
}

/**
 * POD商品设计（模板套图）批量：
 * 多印花位时按素材顺序成组（正面/背面各一张为一组），不再把单张复制进全部印花位。
 */
async function runGoodsTemplateBatchWorkflowStep({ groups, params = {}, workflowContext = {}, billingNav = 'goods' }) {
  const stepLabel = goodsWorkflowStepLabel(billingNav);
  const templateIds = Array.isArray(params.imageTemplateIds)
    ? [...new Set(params.imageTemplateIds.map((x) => String(x).trim()).filter(Boolean))]
    : [];
  if (templateIds.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepSelectDesignTemplate', { step: stepLabel }));
  }
  const validGroups = (groups || []).filter((g) => g && g.spaceId && String(g.url || '').trim());
  if (validGroups.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepMissingInputImage', { step: stepLabel }));
  }

  const imageUrlMap = {};
  for (const group of validGroups) {
    const processable = await toProcessableImageUrl(group.url);
    if (!processable) throw new Error(i18n.t('workbench:ipeUtilError.stepStampResultUnresolved', { step: stepLabel }));
    imageUrlMap[String(group.spaceId)] = processable;
  }
  const printIds = validGroups.map((g) => String(g.spaceId));

  const preliminaryForms = [];
  let slotCountForMapping = 1;
  for (const tid of templateIds) {
    const template = await loadHydratedGoodsTemplate(tid, stepLabel);
    const td = template.templateData || {};
    // 与套图分配弹窗一致：按单 SKC（主图+细节）收集印花位，避免全局去重后只剩 1 位
    const names = resolveStampSlotNamesPerSkc(template);
    const slotNames = names.length > 0 ? names : collectOrderedStampNames(td);
    if (slotNames.length === 0) {
      console.warn('[workflow goods batch] skip template without stamp areas', tid);
      continue;
    }
    slotCountForMapping = Math.max(slotCountForMapping, slotNames.length);
    const imageGroups = buildChunkedStampImageGroups(slotNames, validGroups);
    preliminaryForms.push({
      templateId: String(template.id),
      templateName: template.name || `模版 ${template.id}`,
      templateData: td,
      imageGroups,
      compositeResults: [],
    });
  }
  if (preliminaryForms.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepNoStampAreas', { step: stepLabel }));
  }

  const imageGroupCount = Math.max(
    1,
    ...preliminaryForms.map((form) => (
      Array.isArray(form.imageGroups) ? form.imageGroups.length : 0
    )),
  );
  const previewSource = previewUrlForTask(validGroups[0].url) || previewUrlForTask(imageUrlMap[printIds[0]]);
  const { taskId, finalForms } = await executeTemplateGoodsComposite({
    preliminaryForms,
    printIds,
    previewSource,
    billingNav,
    workflowContext,
    extraInitialResult: {
      printSource: 'workflow-stamp-batch',
      groupRowIds: validGroups.map((g, i) => g.rowId ?? i),
      stampSlotCount: slotCountForMapping,
      stampImageGroupCount: imageGroupCount,
    },
    stepLabel,
    groupCount: Math.max(1, estimateUnitsFromGoodsForms(preliminaryForms) || imageGroupCount),
    imageUrlMap,
  });

  // 同一印花组内的多张素材共享该组合成结果（按条目顺序回填）
  const savedByListingGroup = new Map();
  const byGroup = [];
  for (let materialIndex = 0; materialIndex < validGroups.length; materialIndex += 1) {
    const group = validGroups[materialIndex];
    const listingGroupIndex = Math.min(
      Math.floor(materialIndex / Math.max(1, slotCountForMapping)),
      imageGroupCount - 1,
    );
    let cached = savedByListingGroup.get(listingGroupIndex);
    if (!cached) {
      const groupUrls = collectCompositeUrlsForGroup(finalForms, listingGroupIndex);
      const mainEntries = collectMainCompositeEntriesForGroup(finalForms, listingGroupIndex);
      const mainUrls = mainEntries.map((row) => row.url);
      if (groupUrls.length === 0) {
        cached = {
          failed: true,
          error: i18n.t('workbench:ipeUtilError.stampGroupNoComposite'),
        };
      } else {
        // 主图创作只带主图位；预览/落盘也优先主图，禁止把细节图塞进 mainCompositeImageUrls
        const previewUrl = mainUrls[0] || groupUrls[0];
        const saved = await saveWorkflowImageToSpace(
          `wf-goods-g${listingGroupIndex}-${group.rowId ?? materialIndex}`,
          previewUrl,
          '工作流-套图',
        );
        cached = {
          listingGroupIndex,
          mainCompositeImageUrls: mainUrls,
          mainCompositeMetas: mainEntries,
          ...attachWorkflowImagePreview(saved, groupUrls),
        };
      }
      savedByListingGroup.set(listingGroupIndex, cached);
    }
    if (cached.failed) {
      byGroup.push({
        rowId: group.rowId,
        taskId,
        failed: true,
        error: cached.error,
      });
      continue;
    }
    byGroup.push({
      rowId: group.rowId,
      taskId,
      listingGroupIndex: cached.listingGroupIndex,
      mainCompositeImageUrls: cached.mainCompositeImageUrls || [],
      mainCompositeMetas: cached.mainCompositeMetas || [],
      ...cached,
    });
  }

  return { taskId, byGroup };
}

/** 商品/主图/POD 阶段批量入口：一个任务批次承载全部印花组。 */
export async function runGoodsBatchWorkflowStep({
  groups = [],
  params = {},
  workflowContext = {},
  billingNav = 'goods',
}) {
  const useAi = shouldUseAiGoodsStudio(billingNav, params);
  if (useAi) {
    return runGoodsAiBatchWorkflowStep({ groups, params, workflowContext, billingNav });
  }
  return runGoodsTemplateBatchWorkflowStep({ groups, params, workflowContext, billingNav });
}
